import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { VoucherType } from '../../types'
import FilePreviewButton from '../Common/FilePreviewButton'
import ApprovalStages from '../Common/ApprovalStages'
import { formatPaymentDate } from '../../utils/utils'
import theme from '../../theme'

const primaryTint = alpha(theme.palette.primary.main, 0.07)
const primaryTintHover = alpha(theme.palette.primary.main, 0.12)

type VoucherExpandedRowProps = {
  voucher: VoucherType
  showPaymentInfo?: boolean
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    sx={{
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 1,
    }}
  >
    {children}
  </Typography>
)

const PayField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography
      sx={{
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.92rem', fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
)

const VoucherExpandedRow: React.FC<VoucherExpandedRowProps> = ({
  voucher,
  showPaymentInfo = false,
}) => {
  const hasDescription = !!voucher.description?.trim()

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <ApprovalStages voucher={voucher} variant="compact" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: showPaymentInfo ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
          gap: 3,
          mt: 2,
        }}
      >
        <Box>
          <SectionLabel>Description</SectionLabel>
          <Box
            sx={{
              borderLeft: 3,
              borderColor: 'primary.main',
              bgcolor: primaryTint,
              borderRadius: '0 8px 8px 0',
              px: 2,
              py: 1.25,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
              color={hasDescription ? 'text.primary' : 'text.secondary'}
            >
              {hasDescription ? voucher.description : 'No description provided.'}
            </Typography>
          </Box>
        </Box>

        {showPaymentInfo && (
          <Box>
            <SectionLabel>Payment Information</SectionLabel>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <PayField label="Pay to" value={voucher.pay_to || '—'} />
              <PayField
                label="Payment date"
                value={formatPaymentDate(voucher.paid_by_amin) || '—'}
              />
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <SectionLabel>Cost Categories</SectionLabel>
        {voucher.voucher_cost_category.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No cost categories added.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {voucher.voucher_cost_category.map((cat, index) => (
              <Box
                key={cat.id || index}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderTop: 3,
                  borderTopColor: 'secondary.main',
                  borderRadius: 2,
                  px: 1.75,
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }} noWrap>
                      {cat.sub_cost_category.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }} noWrap>
                      {cat.sub_cost_category.cost_category?.name} · {cat.sub_cost_category.code}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {cat.cost_share} {voucher.currency}
                  </Typography>
                </Box>

                {cat.comment && (
                  <Typography
                    sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    &ldquo;{cat.comment}&rdquo;
                  </Typography>
                )}

                {cat.receipts && cat.receipts.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    {cat.receipts.map((r, rIndex) => (
                      <Box
                        key={r.id || rIndex}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 100,
                          px: 1.1,
                          py: 0.3,
                          fontSize: '0.78rem',
                          '&:hover': { bgcolor: primaryTintHover },
                          '& .MuiLink-root': {
                            color: 'inherit',
                            textDecoration: 'none',
                            fontSize: 'inherit',
                            fontWeight: 500,
                          },
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <FilePreviewButton fileId={r.id} fileName={r.path}>
                          {r.path}
                        </FilePreviewButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default VoucherExpandedRow
